import { Component, ElementRef, HostListener, Injector, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, afterNextRender, computed, inject, input, output, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { AsiakkaanTuote, LaskuTuotteetHakuPyynto, LaskuTuotteetHakuVastaus, LaskunAlv, LaskunTyypit } from '@lemon/core-firebase/model/lasku'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { EMPTY, Subject, from } from 'rxjs'
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../../_angular/service/firebase-lemonaid.service'
import { arvonMuutokset, asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { AlvService } from '../../../_jaettu-angular/service/alv.service'
import { TuoteDialogiService } from '../tuote-dialogi.service'
import { annaNaytettavaTuote, NaytettavaTuote } from '../tuoterekisteri-logiikka'

@Component({
  selector: 'app-tuoterekisteri-haku',
  templateUrl: './tuoterekisteri-haku.component.html',
  styleUrls: ['./tuoterekisteri-haku.component.css'],
  standalone: false,
})
export class TuoterekisteriHakuComponent implements OnInit, OnChanges, OnDestroy {
  readonly avoinna = input(false)
  readonly suljettu = output<void>()

  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      afterNextRender(() => kentta.nativeElement.focus(), { injector: this._injector })
    }
  }

  readonly hakuteksti = form(signal(''))
  readonly hakusana = computed(() => this.hakuteksti().value())
  readonly tulokset = signal<NaytettavaTuote[]>([])
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  readonly sivu = signal(0)
  readonly sivunKoko = signal(50)
  readonly sivukoot = [50, 100, 200]

  private readonly _injector = inject(Injector)
  private _hakuTrigger = new Subject<void>()
  private _edellinenHakuJson: string | null = null
  private _ngUnsubscribe = new Subject<void>()
  private _kotimaanAlvt: Promise<LaskunAlv[]>

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _tuoteDialogiService: TuoteDialogiService,
    private _alvService: AlvService,
    private _currencyService: CurrencyService,
  ) {
    arvonMuutokset(this.hakuteksti).subscribe(() => this.hakutekstiMuuttui())
  }

  ngOnInit() {
    this._kotimaanAlvt = this._alvService.annaLaskutyypinAlvt(LaskunTyypit.TAVALLINEN, null)

    this._hakuTrigger
      .pipe(
        debounceTime(500),
        switchMap(() => {
          if (!this.hakuteksti().value().trim()) {
            this.haetaan.set(false)
            this.tulokset.set([])
            this.kokonaismaara.set(0)
            this._edellinenHakuJson = null
            return EMPTY
          }
          const pyynto = this._rakennaHakupyynto()
          const pyyntoJson = JSON.stringify(pyynto)
          if (pyyntoJson === this._edellinenHakuJson) {
            this.haetaan.set(false)
            return EMPTY
          }
          return from(this._suoritaHaku(pyynto)).pipe(map(vastaus => ({ vastaus, pyyntoJson })))
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(({ vastaus, pyyntoJson }) => this._kasitteleHakuvastaus(vastaus, pyyntoJson))
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this._hakuTrigger.next()
    } else {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (this.avoinna()) {
      this.sulje()
    }
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this.sivu.set(0)
    this._hakuTrigger.next()
  }

  tyhjennaHaku() {
    asetaArvo(this.hakuteksti, '', { ilmoita: false })
    this.hakutekstiMuuttui()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu.set(event.pageIndex)
    this.sivunKoko.set(event.pageSize)
    this._hakuTrigger.next()
  }

  async avaaTuote(tuote: AsiakkaanTuote) {
    const muuttui = await this._tuoteDialogiService.avaaTuoterekisterinTuote(tuote)
    if (muuttui) {
      this._edellinenHakuJson = null
      this._hakuTrigger.next()
    }
  }

  private async _suoritaHaku(pyynto: LaskuTuotteetHakuPyynto): Promise<{ tuotteet: NaytettavaTuote[]; kokonaismaara: number } | null> {
    this.haetaan.set(true)
    try {
      const [vastaus, kotimaanAlvt] = await Promise.all([this._firebaseLemonaid.functionsCall<LaskuTuotteetHakuPyynto, LaskuTuotteetHakuVastaus>('laskutEtsiTuote', pyynto, undefined, 'europe-north1'), this._kotimaanAlvt])
      if (!vastaus || vastaus.e) {
        return null
      }
      return { tuotteet: (vastaus.tuotteet ?? []).map(tuote => annaNaytettavaTuote(tuote, kotimaanAlvt, this._currencyService)), kokonaismaara: vastaus.kokonaismaara ?? 0 }
    } catch {
      return null
    }
  }

  private _kasitteleHakuvastaus(vastaus: { tuotteet: NaytettavaTuote[]; kokonaismaara: number } | null, pyyntoJson: string) {
    this.haetaan.set(false)
    if (!vastaus) {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
      return
    }
    this._edellinenHakuJson = pyyntoJson
    this.tulokset.set(vastaus.tuotteet)
    this.kokonaismaara.set(vastaus.kokonaismaara)
  }

  private _rakennaHakupyynto(): LaskuTuotteetHakuPyynto {
    const pyynto: LaskuTuotteetHakuPyynto = { ylaraja: this.sivunKoko(), sivu: this.sivu() }
    const teksti = this.hakuteksti().value().trim()
    if (teksti) {
      pyynto.vapaahaku = teksti
    }
    return pyynto
  }
}
